/**
 * Currency — which currency the open business uses, and how to show money.
 *
 * Eaze runs across Africa: never write "GH₵" or "GHS" in a screen. Use:
 *   - <Money minor|major />           in JSX (follows the display switcher)
 *   - useMoneyFormat()                when you need a string in a component
 *   - formatMoney(minor) / formatMajor(major)   outside React (utils, print)
 *   - useCurrency().symbol            for input labels ("Price (KSh)")
 *
 * The amount is always in the BUSINESS currency (what the books hold).
 * Receipts, tills and inputs use it directly; the header's display switcher
 * (DisplayCurrencyContext) only converts read-only views.
 *
 * Storage is ×100 minor units for every currency; currencies with no coins
 * in daily use (UGX, RWF, XOF…) just show no decimals.
 * Mirrors eaze-backend/src/utils/currency.js — keep the two tables in step.
 */
import { create } from "zustand";

export interface CurrencyInfo {
  code: string;
  symbol: string;
  decimals: number;
  name: string;
}

const TABLE: Record<string, [symbol: string, decimals: number, name: string]> = {
  GHS: ["GH₵", 2, "Ghana cedi"],
  NGN: ["₦", 2, "Nigerian naira"],
  KES: ["KSh", 2, "Kenyan shilling"],
  ZAR: ["R", 2, "South African rand"],
  UGX: ["USh", 0, "Ugandan shilling"],
  TZS: ["TSh", 0, "Tanzanian shilling"],
  RWF: ["FRw", 0, "Rwandan franc"],
  BIF: ["FBu", 0, "Burundian franc"],
  ETB: ["Br", 2, "Ethiopian birr"],
  EGP: ["E£", 2, "Egyptian pound"],
  MAD: ["DH", 2, "Moroccan dirham"],
  DZD: ["DA", 2, "Algerian dinar"],
  TND: ["DT", 2, "Tunisian dinar"],
  LYD: ["LD", 2, "Libyan dinar"],
  SDG: ["SDG", 2, "Sudanese pound"],
  SSP: ["SSP", 2, "South Sudanese pound"],
  ZMW: ["K", 2, "Zambian kwacha"],
  MWK: ["MK", 2, "Malawian kwacha"],
  MZN: ["MT", 2, "Mozambican metical"],
  AOA: ["Kz", 2, "Angolan kwanza"],
  BWP: ["P", 2, "Botswana pula"],
  NAD: ["N$", 2, "Namibian dollar"],
  LSL: ["L", 2, "Lesotho loti"],
  SZL: ["E", 2, "Swazi lilangeni"],
  MGA: ["Ar", 0, "Malagasy ariary"],
  MUR: ["Rs", 2, "Mauritian rupee"],
  SCR: ["SR", 2, "Seychellois rupee"],
  KMF: ["CF", 0, "Comorian franc"],
  DJF: ["Fdj", 0, "Djiboutian franc"],
  SOS: ["Sh", 2, "Somali shilling"],
  ERN: ["Nfk", 2, "Eritrean nakfa"],
  CDF: ["FC", 2, "Congolese franc"],
  SLE: ["Le", 2, "Sierra Leonean leone"],
  LRD: ["L$", 2, "Liberian dollar"],
  GMD: ["D", 2, "Gambian dalasi"],
  GNF: ["FG", 0, "Guinean franc"],
  CVE: ["Esc", 2, "Cape Verdean escudo"],
  MRU: ["UM", 2, "Mauritanian ouguiya"],
  STN: ["Db", 2, "São Tomé dobra"],
  XOF: ["CFA", 0, "West African CFA franc"],
  XAF: ["FCFA", 0, "Central African CFA franc"],
  USD: ["$", 2, "US dollar"],
  EUR: ["€", 2, "Euro"],
  GBP: ["£", 2, "British pound"],
};

/** Countries we sell in → their currency (used by sign-up). */
export const COUNTRIES: { code: string; name: string; currency: string }[] = [
  { code: "GH", name: "Ghana", currency: "GHS" },
  { code: "NG", name: "Nigeria", currency: "NGN" },
  { code: "KE", name: "Kenya", currency: "KES" },
  { code: "ZA", name: "South Africa", currency: "ZAR" },
  { code: "UG", name: "Uganda", currency: "UGX" },
  { code: "TZ", name: "Tanzania", currency: "TZS" },
  { code: "RW", name: "Rwanda", currency: "RWF" },
  { code: "CI", name: "Côte d'Ivoire", currency: "XOF" },
  { code: "SN", name: "Senegal", currency: "XOF" },
  { code: "BJ", name: "Benin", currency: "XOF" },
  { code: "TG", name: "Togo", currency: "XOF" },
  { code: "BF", name: "Burkina Faso", currency: "XOF" },
  { code: "ML", name: "Mali", currency: "XOF" },
  { code: "NE", name: "Niger", currency: "XOF" },
  { code: "CM", name: "Cameroon", currency: "XAF" },
  { code: "GA", name: "Gabon", currency: "XAF" },
  { code: "CG", name: "Congo", currency: "XAF" },
  { code: "CD", name: "DR Congo", currency: "CDF" },
  { code: "ET", name: "Ethiopia", currency: "ETB" },
  { code: "EG", name: "Egypt", currency: "EGP" },
  { code: "MA", name: "Morocco", currency: "MAD" },
  { code: "ZM", name: "Zambia", currency: "ZMW" },
  { code: "MW", name: "Malawi", currency: "MWK" },
  { code: "MZ", name: "Mozambique", currency: "MZN" },
  { code: "AO", name: "Angola", currency: "AOA" },
  { code: "ZW", name: "Zimbabwe", currency: "USD" },
  { code: "BW", name: "Botswana", currency: "BWP" },
  { code: "NA", name: "Namibia", currency: "NAD" },
  { code: "SL", name: "Sierra Leone", currency: "SLE" },
  { code: "LR", name: "Liberia", currency: "LRD" },
  { code: "GM", name: "The Gambia", currency: "GMD" },
  { code: "GN", name: "Guinea", currency: "GNF" },
  { code: "MU", name: "Mauritius", currency: "MUR" },
  { code: "MG", name: "Madagascar", currency: "MGA" },
];

export const DEFAULT_CURRENCY = "GHS";

export function currencyInfo(code?: string | null): CurrencyInfo {
  const c = (code || "").trim().toUpperCase();
  const row = TABLE[c];
  if (row) return { code: c, symbol: row[0], decimals: row[1], name: row[2] };
  if (/^[A-Z]{3}$/.test(c)) return { code: c, symbol: c, decimals: 2, name: c };
  const d = TABLE[DEFAULT_CURRENCY];
  return { code: DEFAULT_CURRENCY, symbol: d[0], decimals: d[1], name: d[2] };
}

export interface MoneyFormatOptions {
  /** 1.2M once the amount reaches a million. */
  compact?: boolean;
  /** Axis labels: 45K, 1.2M, no decimals. */
  short?: boolean;
  /** No decimals — for rounded estimates ("≈ ₦28,400"). */
  whole?: boolean;
}

/** Symbol followed by a space when it's letters ("KSh 50", "CFA 500"), else glued ("₦50"). */
export function moneyPrefix(info: CurrencyInfo): string {
  return /^[A-Za-z]{2,}$/.test(info.symbol) ? `${info.symbol} ` : info.symbol;
}

export function formatAmount(major: number, code?: string | null, opts: MoneyFormatOptions = {}): string {
  const info = currencyInfo(code ?? getCurrencyCode());
  const n = Number.isFinite(major) ? major : 0;
  const abs = Math.abs(n);
  // Chart axes: short labels ("45K", "1.2M", "800") so ticks don't crowd.
  if (opts.short) {
    const shortBody =
      abs >= 1000
        ? new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(abs)
        : Math.round(abs).toLocaleString("en-US");
    return `${n < 0 ? "-" : ""}${moneyPrefix(info)}${shortBody}`;
  }
  const body =
    opts.compact && abs >= 1_000_000
      ? new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 2 }).format(abs)
      : abs.toLocaleString("en-US", { minimumFractionDigits: opts.whole ? 0 : info.decimals, maximumFractionDigits: opts.whole ? 0 : info.decimals });
  return `${n < 0 ? "-" : ""}${moneyPrefix(info)}${body}`;
}

// ── The open business's currency (set by DisplayCurrencyProvider) ──

const STORAGE_KEY = "eaze:business-currency";

interface CurrencyState {
  code: string;
  setCode: (code: string) => void;
}

function initialCode(): string {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && /^[A-Z]{3}$/.test(v)) return v;
  } catch {
    /* private window — fine */
  }
  return DEFAULT_CURRENCY;
}

export const useCurrencyStore = create<CurrencyState>((set) => ({
  code: initialCode(),
  setCode: (code) => {
    const c = currencyInfo(code).code;
    try {
      localStorage.setItem(STORAGE_KEY, c); // remembered for offline / reloads
    } catch {
      /* ignore */
    }
    set({ code: c });
  },
}));

export const getCurrencyCode = () => useCurrencyStore.getState().code;

/** Minor units (×100) in the business currency → "GH₵12.50" / "USh 1,250". */
export const formatMoney = (minor: number | null | undefined, code?: string | null) =>
  formatAmount((Number(minor) || 0) / 100, code);

/** Major units (12.5) in the business currency. */
export const formatMajor = (major: number | null | undefined, code?: string | null) =>
  formatAmount(Number(major) || 0, code);

/** The open business's currency, for labels and inputs. Re-renders on change. */
export function useCurrency(): CurrencyInfo & {
  minor: (v: number | null | undefined) => string;
  major: (v: number | null | undefined) => string;
} {
  const code = useCurrencyStore((s) => s.code);
  const info = currencyInfo(code);
  return { ...info, minor: (v) => formatMoney(v, code), major: (v) => formatMajor(v, code) };
}

/** The open business's currency symbol, for labels outside hooks. */
export const curSymbol = () => currencyInfo(getCurrencyCode()).symbol;

/**
 * Locale for numbers and dates — English with the business's country
 * ("en-NG", "en-KE"…), from its currency. Same digits and separators across
 * Anglophone Africa; keeps the app's English while dropping the Ghana default.
 */
export function appLocale(): string {
  const code = getCurrencyCode();
  const country = COUNTRIES.find((c) => c.currency === code)?.code || "GH";
  return `en-${country}`;
}

/** An amount (minor units) as the text for a money input: "1250.50", or "1250" for currencies without coins (UGX, TZS, XOF…). */
export function toMoneyInput(minor: number | null | undefined, code?: string | null): string {
  return ((Number(minor) || 0) / 100).toFixed(currencyInfo(code).decimals);
}

/** Step and placeholder for a money input in this currency. */
export function moneyInputProps(code?: string | null): { step: string; placeholder: string } {
  const d = currencyInfo(code).decimals;
  return d === 0 ? { step: "1", placeholder: "0" } : { step: "0.01", placeholder: "0.00" };
}
